import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import { DEST_PAGES } from "@/lib/destinations";
import { waLink, WA_MESSAGES } from "@/lib/site";
import { Reveal, ArrowButton, ChapterLabel } from "@/components/landing/Reveal";

const DestinationPage = () => {
    const { slug } = useParams();
    const d = DEST_PAGES[slug];

    useEffect(() => {
        if (d) {
            document.title = d.title;
            document
                .querySelector('meta[name="description"]')
                ?.setAttribute("content", d.meta);
        }
        return () => {
            document.title = "BookMyVIP | VIP Table Booking in Thailand";
        };
    }, [d]);

    if (!d) return <Navigate to="/" replace />;

    const others = Object.values(DEST_PAGES).filter((x) => x.slug !== slug);

    return (
        <main data-testid={`destination-page-${slug}`}>
            <section className="relative flex min-h-[75svh] items-end overflow-hidden">
                <img
                    src={d.image}
                    alt={`${d.city} nightlife`}
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-black/50" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-[#050505]/40" />
                <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-20 pt-40 md:px-10">
                    <Reveal>
                        <Link
                            data-testid="destination-back-link"
                            to="/#destinations"
                            className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-ivory/60 transition-colors duration-300 hover:text-gold"
                        >
                            <ArrowLeft size={14} strokeWidth={1.5} /> All Destinations
                        </Link>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h1 className="mt-8 font-serif text-4xl font-medium leading-[1.02] tracking-tight text-ivory md:text-7xl">
                            VIP TABLES IN{" "}
                            <span className="italic text-gold">{d.city.toUpperCase()}.</span>
                        </h1>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="mt-6 font-serif text-xl italic text-ivory/75 md:text-2xl">
                            {d.tagline}
                        </p>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <div className="mt-10 flex flex-col gap-4 sm:flex-row">
                            <ArrowButton
                                testId="destination-book-vip-btn"
                                href={waLink(WA_MESSAGES.destination(d.city))}
                            >
                                Book VIP in {d.city}
                            </ArrowButton>
                            <ArrowButton
                                testId="destination-concierge-btn"
                                variant="secondary"
                                href={waLink(WA_MESSAGES.concierge)}
                            >
                                WhatsApp Concierge
                            </ArrowButton>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-5 py-24 md:px-10 md:py-32">
                <div className="grid gap-14 md:grid-cols-2 md:gap-20">
                    <div>
                        <Reveal>
                            <ChapterLabel number="Nº 03" title={`The ${d.city} Night`} testId="destination-intro-label" />
                        </Reveal>
                        {d.intro.map((p, i) => (
                            <Reveal key={i} delay={0.1 + i * 0.08}>
                                <p className="mt-8 text-sm leading-relaxed text-muted md:text-base">
                                    {p}
                                </p>
                            </Reveal>
                        ))}
                        <Reveal delay={0.25}>
                            <div className="mt-10 flex flex-wrap gap-3">
                                {d.areas.map((a) => (
                                    <span
                                        key={a}
                                        data-testid={`destination-area-${a.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                        className="border border-white/15 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-ivory/70"
                                    >
                                        {a}
                                    </span>
                                ))}
                            </div>
                        </Reveal>
                    </div>
                    <Reveal delay={0.15}>
                        <div className="rounded-sm border border-white/10 bg-[#0F0F0F] p-8 md:p-10">
                            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold">
                                What we arrange
                            </p>
                            <ul className="mt-8 space-y-6">
                                {d.arrange.map((item) => (
                                    <li key={item} className="flex items-start gap-4">
                                        <Check size={16} strokeWidth={1.5} className="mt-0.5 shrink-0 text-gold" />
                                        <span className="text-sm leading-relaxed text-ivory/85">
                                            {item}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                            <a
                                data-testid="destination-card-whatsapp-btn"
                                href={waLink(WA_MESSAGES.destination(d.city))}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Opens WhatsApp"
                                className="mt-10 inline-flex w-full items-center justify-center rounded-sm bg-gold px-8 py-4 text-[11px] font-bold uppercase tracking-[0.25em] text-ink transition-colors duration-300 hover:bg-gold-soft"
                            >
                                Ask About {d.city}
                            </a>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="border-t border-white/10 bg-[#0A0A0A]">
                <div className="mx-auto max-w-7xl px-5 py-20 md:px-10 md:py-28">
                    <Reveal>
                        <h2 className="font-serif text-2xl font-medium tracking-tight text-ivory md:text-4xl">
                            OR PICK <span className="italic text-gold">ANOTHER CITY.</span>
                        </h2>
                    </Reveal>
                    <div className="mt-10 grid gap-6 md:grid-cols-3">
                        {others.map((o, i) => (
                            <Reveal key={o.slug} delay={0.05 + i * 0.06}>
                                <Link
                                    data-testid={`destination-other-${o.slug}`}
                                    to={`/destinations/${o.slug}`}
                                    className="card-zoom group relative block h-[240px] overflow-hidden rounded-sm border border-white/10"
                                >
                                    <img
                                        src={o.image}
                                        alt={`${o.city} nightlife`}
                                        loading="lazy"
                                        className="absolute inset-0 h-full w-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10" />
                                    <div className="absolute inset-x-0 bottom-0 p-6">
                                        <span className="font-serif text-2xl italic text-ivory transition-colors duration-300 group-hover:text-gold">
                                            {o.city}
                                        </span>
                                    </div>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>
        </main>
    );
};

export default DestinationPage;
